import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';

import { twcn } from '@web/ui/core/utils';

/*
 * Switch — Radix Switch wrapped with the DS v2 toggle styling.
 *
 * Examples:
 *   <Switch checked={enabled} onCheckedChange={setEnabled} />
 *   <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Auto-join" />
 *
 * Visuals follow the design tokens (`bg-border` off, `bg-primary` on) so
 * the component drops into any surface without overrides.
 */
export type SwitchProps = ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>;

export const Switch = forwardRef<ComponentRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  function Switch({ className, ...props }, ref) {
    return (
      <SwitchPrimitive.Root
        ref={ref}
        className={twcn(
          'peer relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-pill border-0 bg-border transition-colors duration-base ease-standard',
          'data-[state=checked]:bg-primary',
          'hover:shadow-[0_0_0_4px_hsl(var(--foreground)/0.05)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={twcn(
            'pointer-events-none block h-4 w-4 translate-x-0.5 rounded-pill bg-card shadow-[0_1px_2px_rgba(0,0,0,0.18)] transition-transform duration-base ease-standard',
            'data-[state=checked]:translate-x-4',
          )}
        />
      </SwitchPrimitive.Root>
    );
  },
);
